import React, { useState } from 'react';
import { inventoryAPI } from '../services/api';

export default function CSVUpload() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setError(null);
    } else {
      setError('Please select a valid CSV file');
      setFile(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const response = await inventoryAPI.uploadCSV(file);
      setResult(response.data);
      setFile(null);
      // Reset file input
      document.getElementById('csvFile').value = '';
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed');
      if (err.response?.data?.errors) {
        setError(prev => `${prev}\n${err.response.data.errors.join('\n')}`);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await inventoryAPI.downloadTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'inventory_template.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      console.log('Template downloaded successfully');
    } catch (err) {
      console.error('Download error:', err);
      setError(`Failed to download template: ${err.message || 'Unknown error'}`);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-6">Bulk Upload Inventory</h2>

      {/* Download Template */}
      <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-sm text-gray-700 mb-3">
          Need help? Download the CSV template with sample data
        </p>
        <button
          onClick={handleDownloadTemplate}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
        >
          📥 Download Template
        </button>
      </div>

      {/* File Upload */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Select CSV File
        </label>
        <div className="flex gap-3">
          <input
            id="csvFile"
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            disabled={uploading}
            className="flex-1 px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 outline-none"
          />
          <button
            onClick={handleUpload}
            disabled={!file || uploading}
            className="px-6 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-400"
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
        {file && (
          <p className="text-sm text-gray-600 mt-2">
            Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)
          </p>
        )}
      </div>

      {/* Error Messages */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded">
          <p className="text-red-700 text-sm font-medium">Error:</p>
          <p className="text-red-600 text-sm mt-1 whitespace-pre-wrap">{error}</p>
        </div>
      )}

      {/* Success Result */}
      {result && result.success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded">
          <p className="text-green-700 font-medium mb-3">{result.message}</p>
          
          {result.created > 0 && (
            <div className="mb-3">
              <p className="text-green-700 text-sm font-medium">✓ Created: {result.created} items</p>
              <ul className="text-sm text-green-600 mt-2 space-y-1">
                {result.created_items?.slice(0, 5).map((item, idx) => (
                  <li key={idx}>• {item.name}</li>
                ))}
                {result.created_items?.length > 5 && (
                  <li>• ... and {result.created_items.length - 5} more</li>
                )}
              </ul>
            </div>
          )}

          {result.failed > 0 && (
            <div>
              <p className="text-orange-700 text-sm font-medium">⚠ Failed: {result.failed} items</p>
              <ul className="text-sm text-orange-600 mt-2 space-y-1">
                {result.failed_items?.slice(0, 5).map((item, idx) => (
                  <li key={idx}>• {item.name}: {item.error}</li>
                ))}
                {result.failed_items?.length > 5 && (
                  <li>• ... and {result.failed_items.length - 5} more</li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* CSV Format Info */}
      <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h3 className="font-medium text-gray-800 mb-3">CSV Format Requirements</h3>
        <table className="w-full text-sm text-gray-700">
          <thead className="bg-gray-100 border-b border-gray-300">
            <tr>
              <th className="px-3 py-2 text-left">Column</th>
              <th className="px-3 py-2 text-left">Type</th>
              <th className="px-3 py-2 text-left">Rules</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-200">
              <td className="px-3 py-2 font-medium">name *</td>
              <td className="px-3 py-2">Text</td>
              <td className="px-3 py-2">Required, max 100 chars</td>
            </tr>
            <tr className="border-b border-gray-200">
              <td className="px-3 py-2 font-medium">category *</td>
              <td className="px-3 py-2">Text</td>
              <td className="px-3 py-2">Required, max 50 chars</td>
            </tr>
            <tr className="border-b border-gray-200">
              <td className="px-3 py-2 font-medium">price *</td>
              <td className="px-3 py-2">Number</td>
              <td className="px-3 py-2">Required, 0.01 - 100000</td>
            </tr>
            <tr className="border-b border-gray-200">
              <td className="px-3 py-2 font-medium">stock *</td>
              <td className="px-3 py-2">Integer</td>
              <td className="px-3 py-2">Required, 0 - 100000</td>
            </tr>
            <tr className="border-b border-gray-200">
              <td className="px-3 py-2">description</td>
              <td className="px-3 py-2">Text</td>
              <td className="px-3 py-2">Optional, max 500 chars</td>
            </tr>
            <tr className="border-b border-gray-200">
              <td className="px-3 py-2">sku</td>
              <td className="px-3 py-2">Text</td>
              <td className="px-3 py-2">Optional</td>
            </tr>
            <tr>
              <td className="px-3 py-2">supplier</td>
              <td className="px-3 py-2">Text</td>
              <td className="px-3 py-2">Optional</td>
            </tr>
          </tbody>
        </table>
        <p className="text-xs text-gray-600 mt-3">* Required fields</p>
      </div>
    </div>
  );
}
